/**
 * 基准点可视对象。
 * 职责：
 * 1. 直接以 `THREE.Sprite` 承载基准点显示；
 * 2. 标签对象独立创建，由管理器统一挂载到专用标签组；
 * 3. 不承载求解逻辑，仅负责最终可视化表达。
 */

import * as THREE from 'three/webgpu';
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {PointsMaterialFactory} from '../material/PointsMaterialFactory';
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {ReferenceCreationContext} from './ReferenceCreationContext';


/** 基准点稳定业务 ID 计数器。 */
let datumPointIdCount = 0;


/**
 * 判断对象是否为基准点。
 *
 * @param value 待判断对象。
 * @returns 是否为基准点对象。
 */
export function isDatumPoint(value: unknown): value is DatumPoint {
  return Reflect.get(value as object, 'isDatumPoint') === true;
}


/**
 * 读取对象所属的正式基准点稳定 ID。
 *
 * @param value 待读取对象。
 * @returns 基准点稳定 ID；对象不属于正式基准点时返回 `null`。
 */
export function getDatumPointIdFromObject(value: THREE.Object3D): number | null {
  const pointId = Reflect.get(value, 'datumPointId');
  return Number.isFinite(pointId) ? Number(pointId) : null;
}


/**
 * 基准点构造参数。
 */
export interface DatumPointOptions {

  /** 基准点数值 ID；仅历史恢复时传入。 */
  id?: number;

  /** 创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 基准点名称。 */
  name: string;
  /** 世界坐标。 */
  position: THREE.Vector3;

  /** 是否可见。 */
  visible?: boolean;

  /** 是否为默认原点。 */
  isOrigin?: boolean;
  /** 用户手动创建时间戳（毫秒），用于设计树时间线排序与历史恢复。 */
  createdAt?: number;
}

/**
 * 基准点。
 */
export class DatumPoint extends THREE.Sprite {

  /**
   * 基准点标签世界坐标偏移。
   *
   * 说明：场景单位为米，0.01 表示 10mm；标签仍使用模型空间偏移，
   * 但不再使用原先 1m 级偏移，避免小尺寸零件中标签明显飘离点位。
   */
  private static readonly LABEL_WORLD_OFFSET = new THREE.Vector3(0.01, 0.01, 0);

  /** 运行时类型标记。 */
  public readonly isDatumPoint: boolean = true;

  /** 基准点稳定业务 ID。 */
  public readonly datumPointId: number;

  /** 是否为默认原点。 */
  public readonly isOrigin: boolean;
  /** 创建来源上下文。 */
  public readonly creationContext: ReferenceCreationContext;
  /** 标签对象。 */
  private readonly labelObject: CSS2DObject | null;
  /** 标签相对点位的毫米级世界偏移缓存。 */
  private readonly labelOffsetVector: THREE.Vector3;

  /**
   * @param options 构造参数。
   */
  public constructor(options: DatumPointOptions) {
    const material = PointsMaterialFactory.getDatumPointDefaultSpriteMaterial()
    super(material);

    if (Number.isFinite(options.id)) {
      /**
       * 基准点引用必须使用自有稳定 ID，而不能复用 Three.js Object3D.id。
       * Object3D.id 是运行时对象流水号，Redo 后无法作为业务引用真相源。
       */
      this.datumPointId = Number(options.id);
      datumPointIdCount = Math.max(datumPointIdCount, this.datumPointId + 1);
    } else {
      this.datumPointId = datumPointIdCount;
      datumPointIdCount += 1;
    }
    Reflect.set(this, 'datumPointId', this.datumPointId);

    this.isOrigin = options.isOrigin === true;
    this.creationContext = options.creationContext;
    this.labelOffsetVector = DatumPoint.LABEL_WORLD_OFFSET.clone();
    this.name = options.name;
    this.position.copy(options.position.clone());
    this.scale.set(0.008,0.008,0.008);
    this.labelObject = this.isOrigin ? null : this.createLabel();
    this.renderOrder = 3;
    this.frustumCulled = false;

    this.visible = options.visible ?? true;

    material.depthTest = false
    if (this.labelObject) {
      this.labelObject.visible = this.visible;
    }
  }

  /**
   * 创建标签。
   *
   * @param radius 点半径。
   * @returns 标签对象。
   */
  private createLabel(radius: number = 4): CSS2DObject {
    const element = document.createElement('div');
    element.className = 'datum-point-label';
    element.textContent = this.name;
    element.style.color = '#ffffff';
    element.style.fontSize = '12px';
    element.style.fontWeight = 'bold';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.65)';
    element.style.padding = '4px 8px';
    element.style.borderRadius = '4px';
    element.style.pointerEvents = 'none';
    const label = new CSS2DObject(element);

    /**
     * 标签对象由 Manager 挂在独立标签组下，因此这里仍然写入世界坐标。
     * 偏移量控制在毫米级，保持标签与点有轻微间隔且不会出现 1m 级漂移。
     */
    label.position.copy(this.resolveLabelWorldPosition(this.position));

    return label;
  }


  /**
   * 解析标签世界坐标。
   *
   * @param pointWorld 基准点世界坐标。
   * @returns 叠加毫米级偏移后的标签世界坐标。
   */
  private resolveLabelWorldPosition(pointWorld: THREE.Vector3): THREE.Vector3 {
    return pointWorld.clone().add(this.labelOffsetVector);
  }


  /**
   * 添加到场景。
   *
   * @param scene 场景对象。
   */
  public addToScene(scene: THREE.Scene): void {
    addObject3DToParent(this, scene);
  }

  /**
   * 从场景移除。
   *
   * @param scene 场景对象。
   */
  public removeFromScene(scene: THREE.Scene): void {
    scene.remove(this);
  }

  /**
   * 获取可视对象。
   *
   * @returns 当前对象自身。
   */
  public getObject3D(): THREE.Object3D {
    return this;
  }


  /**
   * 获取基准点世界坐标。
   *
   * @returns 当前基准点的世界坐标。
   */
  public getWorldPoint(): THREE.Vector3 {
    return this.getWorldPosition(new THREE.Vector3());
  }


  /**
   * 更新基准点世界位置。
   *
   * 设计说明：
   * 1. 装配体零部件移动后，依赖其拓扑的基准点需要跟随新位置；
   * 2. 这里原地更新 Sprite，避免删除重建导致引用该基准点的后续特征失去稳定 ID；
   * 3. 标签对象挂在独立标签组下，因此需要同步写入标签世界位置。
   *
   * @param position 新世界坐标。
   */
  public updatePosition(position: THREE.Vector3): void {
    this.position.copy(position);
    this.updateMatrixWorld(true);
    if (this.labelObject) {
      this.labelObject.position.copy(this.resolveLabelWorldPosition(position));
    }
  }


  /**
   * 获取标签对象。
   *
   * @returns 标签对象；原点不创建标签时返回 `null`。
   */
  public getLabelObject(): CSS2DObject | null {
    return this.labelObject;
  }

  /**
   * 设置可见性。
   *
   * @param visible 是否可见。
   */
  public setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.labelObject) {
      this.labelObject.visible = visible;
    }
  }

  /**
   * 获取可见性。
   *
   * @returns 是否可见。
   */
  public isVisible(): boolean {
    return this.visible;
  }

  /**
   * 更新名称。
   *
   * @param nextName 新名称。
   */
  public setName(nextName: string): void {
    this.name = nextName;
    if (this.labelObject?.element) {
      this.labelObject.element.textContent = nextName;
    }
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    if (this.material instanceof THREE.Material) {
      this.material.dispose();
    }
  }
}
